<template>
    <div
        :class="[matchTriggerWidth ? 'tw:block tw:w-full' : 'tw:inline-block', { 'is-not-clickable': disabled }]"
        v-bind="$attrs"
    >
        <div
            ref="triggerContainer"
            class="tw:w-full"
            :class="[matchTriggerWidth ? 'tw:block' : 'tw:inline-block', { 'tw:cursor-pointer': !disabled }]"
            aria-haspopup="true"
            :aria-expanded="visible"
            @click.capture="togglePopover"
        >
            <slot name="trigger" :active="visible" />
        </div>
        <Popover
            ref="popover"
            :append-to="resolvedAppendTo"
            @hide="onHide"
            @show="onShow"
        >
            <div v-if="$slots.header" class="e-dropdown-header">
                <slot name="header" />
            </div>
            <div
                :class="[
                    'e-dropdown-content',
                    contentClass,
                    { 'e-dropdown-content--full': matchTriggerWidth },
                ]"
                :style="scrollable ? { maxHeight: resolvedMaxHeight, overflowY: 'auto' } : {}"
            >
                <slot />
            </div>
        </Popover>
    </div>
</template>

<script>
import Popover from 'primevue/popover';
import { computed, nextTick, onBeforeUnmount, provide, ref } from 'vue';
/**
 * EDropdown - Abstraction wrapper recreating the Buefy b-dropdown experience
 *
 * Usage:
 *   <e-dropdown>
 *     <template #trigger>
 *       <e-button>Click me</e-button>
 *     </template>
 *     <e-dropdown-item>Option 1</e-dropdown-item>
 *     <e-dropdown-item>Option 2</e-dropdown-item>
 *     <e-dropdown-item>Option 3</e-dropdown-item>
 *   </e-dropdown>
 *
 *   <e-dropdown aria-role="list">
 *     <template #trigger>
 *       <e-button icon-right="menu-down">
 *         Actions
 *       </e-button>
 *     </template>
 *     <e-dropdown-item aria-role="listitem">Edit</e-dropdown-item>
 *     <e-dropdown-item aria-role="listitem">Delete</e-dropdown-item>
 *   </e-dropdown>
 *
 *   <e-dropdown position="is-bottom-left" hoverable>
 *     <template #trigger>
 *       <e-icon icon="ellipsis-h" />
 *     </template>
 *     <e-dropdown-item>Settings</e-dropdown-item>
 *     <e-dropdown-item separator />
 *     <e-dropdown-item>Logout</e-dropdown-item>
 *   </e-dropdown>
 *
 * All Buefy b-dropdown props, events, and slots are supported.
 *
 * To access the underlying b-dropdown component methods, use the ref:
 *   <e-dropdown ref="myDropdown" />
 *   this.$refs.myDropdown.toggle()
 */
export default {
    name: 'EDropdown',
    components: {
        Popover,
    },
    inheritAttrs: false,
    props: {
        modelValue: {
            type: [String, Number, Boolean, Object, Array],
            default: null,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        scrollable: {
            type: Boolean,
            default: false,
        },
        // Optional, opt-in: extra class applied to the teleported popover content.
        // Lets callers scope styles to a single dropdown instance. No effect when unset.
        contentClass: {
            type: [String, Array, Object],
            default: null,
        },
        maxHeight: {
            type: [String, Number],
            default: 200,
        },
        matchTriggerWidth: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'active-change', 'change'],
    setup(props, { emit }) {
        const popover = ref(null);
        const dropdown = ref(null);
        const triggerContainer = ref(null);
        const visible = ref(false);
        let triggerResizeObserver = null;

        const getTriggerWidth = () => triggerContainer.value?.getBoundingClientRect().width ?? 0;

        // Undefined rather than 'self' so PrimeVue keeps teleporting the popover and positions it against
        // the viewport. Rendering inline mispositions it inside any positioned ancestor.
        const resolvedAppendTo = computed(() => (props.appendToBody ? 'body' : undefined));

        const isInteractiveTriggerChild = target => {
            if (!(target instanceof Element) || !triggerContainer.value?.contains(target)) {
                return false;
            }

            return Boolean(target.closest('[data-e-dropdown-ignore-toggle]'));
        };

        const applyPopoverWidth = () => {
            const container = popover.value?.container;

            if (!container || !triggerContainer.value) {
                return;
            }

            const width = `${getTriggerWidth()}px`;

            container.style.width = width;
            container.style.minWidth = width;
            container.style.maxWidth = width;

            const content = container.querySelector('.e-dropdown-content, .p-popover-content');

            if (content) {
                content.style.width = '100%';
                content.style.maxWidth = '100%';
                content.style.boxSizing = 'border-box';
            }

            popover.value.alignOverlay?.();
        };

        const syncPopoverWidth = () => {
            if (!props.matchTriggerWidth || !triggerContainer.value || !popover.value) {
                return;
            }

            nextTick(() => {
                applyPopoverWidth();
                requestAnimationFrame(applyPopoverWidth);
            });
        };

        const startTriggerResizeObserver = () => {
            if (!props.matchTriggerWidth || typeof ResizeObserver === 'undefined' || !triggerContainer.value) {
                return;
            }

            triggerResizeObserver?.disconnect();
            triggerResizeObserver = new ResizeObserver(() => {
                if (visible.value) {
                    applyPopoverWidth();
                }
            });
            triggerResizeObserver.observe(triggerContainer.value);
        };

        const stopTriggerResizeObserver = () => {
            triggerResizeObserver?.disconnect();
            triggerResizeObserver = null;
        };

        onBeforeUnmount(() => {
            stopTriggerResizeObserver();
        });

        const togglePopover = event => {
            if (props.disabled) return;

            if (isInteractiveTriggerChild(event?.target)) {
                return;
            }

            if (popover.value) {
                if (visible.value) {
                    popover.value.hide();
                } else {
                    popover.value.show(event, triggerContainer.value);
                }
            } else if (dropdown.value) {
                dropdown.value.toggle();
            }
        };

        const close = () => {
            if (popover.value) {
                popover.value.hide();
                visible.value = false;
            } else if (dropdown.value) {
                dropdown.value.isActive = false;
            }
        };

        const selectItem = value => {
            if (props.multiple) {
                const newValue = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
                const index = newValue.findIndex(item => JSON.stringify(item) === JSON.stringify(value));
                if (index > -1) {
                    newValue.splice(index, 1);
                } else {
                    newValue.push(value);
                }
                emit('update:modelValue', newValue);
                emit('change', newValue);
            } else {
                emit('update:modelValue', value);
                emit('change', value);
                close();
            }
        };

        // Provide close method and selectItem to children (EDropdownItem)
        provide('closeDropdown', close);
        provide('selectItem', selectItem);
        provide(
            'modelValue',
            computed(() => props.modelValue)
        );
        provide(
            'multiple',
            computed(() => props.multiple)
        );

        const onShow = () => {
            visible.value = true;
            emit('active-change', true);
            syncPopoverWidth();
            startTriggerResizeObserver();
        };

        const onHide = () => {
            visible.value = false;
            emit('active-change', false);
            stopTriggerResizeObserver();
        };

        const show = event => {
            if (popover.value) {
                const eventObject = event || { currentTarget: triggerContainer.value };
                popover.value.show(eventObject, triggerContainer.value);
            } else if (dropdown.value) {
                dropdown.value.isActive = true;
            }
        };

        const hide = () => {
            if (popover.value) {
                popover.value.hide();
            } else if (dropdown.value) {
                dropdown.value.isActive = false;
            }
        };

        const toggle = event => {
            togglePopover(event);
        };

        return {
            popover,
            dropdown,
            triggerContainer,
            resolvedAppendTo,
            togglePopover,
            visible,
            onShow,
            onHide,
            show,
            hide,
            toggle,
        };
    },
    computed: {
        $el() {
            return this.$refs.dropdown?.$el;
        },

        resolvedMaxHeight() {
            return /^\d+$/.test(String(this.maxHeight)) ? `${this.maxHeight}px` : this.maxHeight;
        },
    },
};
</script>

<style scoped>
.e-dropdown-header {
    width: 100%;
    box-sizing: border-box;
}

.e-dropdown-content--full {
    width: 100%;
    box-sizing: border-box;
}
</style>
